'use client';

import { useState } from 'react';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';
import { Container } from '@/components/primitives/Container/Container';
import { ButtonWrapper } from '@/helpers/Wrappers/ButtonWrapper/ButtonWrapper';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { useMediaQuery } from '@/lib/hooks/useMediaQuery';
import { getCSLPAttributes } from '@/utils/type-guards';
import { trackAdobeClick } from '@/utils/adobe/click';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { ITextOverlayImageCarousel } from '@/.generated';
import { defaultVariants, tabVariants, accordionVariants } from './TextOverlayImageCarousel.styles';

type TextOverlayImageCarouselProps = ITextOverlayImageCarousel & WithMetadata<IBaseComponentProps>;

type CarouselItem = NonNullable<ITextOverlayImageCarousel['items']>[number];

const formatTabNumber = (index: number): string => {
  return String(index + 1).padStart(2, '0');
};

const DesktopView = ({
  items,
  activeIndex,
  onTabChange,
}: {
  items: CarouselItem[];
  activeIndex: number;
  onTabChange: (index: number) => void;
}) => {
  const slots = defaultVariants();
  const activeItem = items[activeIndex];
  // Tabs are only meaningful when there is more than one slide to switch between
  const showTabs = items.length > 1;

  if (!activeItem) return null;

  return (
    <>
      {/* key on activeIndex remounts the slide so the fade-in animation replays on tab change */}
      <div key={activeIndex} className={slots.desktopWrapper()}>
        <div className={slots.imageContainer()}>
          <ImageWrapper
            image={toEnhancedImage(activeItem.desktop_image, {
              alternateText: activeItem.tab_title || 'Carousel image',
              cslp: activeItem.$?.desktop_image,
            })}
            fill
            wrapperClassName="w-full h-full"
            imageClassName={slots.image()}
            sizes="100vw"
            priority
            loading="eager"
          />
        </div>

        <div className={slots.contentOverlay()}>
          <div className={slots.textContent()}>
            {activeItem.eyebrow && (
              <PlainTextWrapper
                tag="p"
                content={activeItem.eyebrow}
                className={slots.eyebrow()}
                cslpAttribute={activeItem.$?.eyebrow}
                isDangerouslySetInnerHTML
              />
            )}
            {activeItem.headline && (
              <PlainTextWrapper
                content={activeItem.headline}
                className={slots.headline()}
                cslpAttribute={activeItem.$?.headline}
                isDangerouslySetInnerHTML
              />
            )}
            {activeItem.body_copy && (
              <div className={slots.bodyCopy()}>
                <RichTextWrapper
                  content={activeItem.body_copy}
                  cslpAttribute={activeItem.$?.body_copy}
                />
              </div>
            )}
            {activeItem.cta?.cta &&
              (activeItem.cta?.cta?.link?.href ||
                (activeItem.cta?.cta?.page_reference &&
                  activeItem.cta?.cta?.page_reference?.length > 0)) && (
                <div className={slots.ctaWrapper()}>
                  <ButtonWrapper cta={activeItem.cta.cta} />
                </div>
              )}
          </div>
        </div>
      </div>
      {showTabs && (
        <div className={slots.tabsContainer()} role="tablist" aria-label="Carousel slides">
          {items.map((item, index) => {
            const isActive = index === activeIndex;
            const tabSlots = tabVariants({ isActive });
            return (
              <button
                key={index}
                type="button"
                className={tabSlots.tabButton()}
                onClick={(e) => {
                  trackAdobeClick({ clickName: item.tab_title || `Tab ${index + 1}` }, e);
                  onTabChange(index);
                }}
                aria-selected={isActive}
                role="tab"
                tabIndex={0}
                {...getCSLPAttributes(item.$?.tab_title)}
              >
                <span className={tabSlots.tabNumber()}>{formatTabNumber(index)}</span>
                <span className={tabSlots.tabTitle()}>{item.tab_title}</span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
};

const MobileView = ({
  items,
  subtitle,
  subtitleCslp,
  openIndex,
  onToggle,
}: {
  items: CarouselItem[];
  subtitle?: string;
  subtitleCslp?: ITextOverlayImageCarousel['$'];
  openIndex: number | null;
  onToggle: (index: number) => void;
}) => {
  const mobileSlots = accordionVariants();
  // With a single item there is nothing to collapse, so skip the accordion toggle entirely
  const isSingleItem = items.length === 1;

  return (
    <div className={mobileSlots.mobileWrapper()}>
      {subtitle && (
        <PlainTextWrapper
          tag="h2"
          content={subtitle}
          className={mobileSlots.subtitle()}
          cslpAttribute={subtitleCslp?.subtitle}
          isDangerouslySetInnerHTML
        />
      )}

      <ul className={mobileSlots.accordionList()}>
        {items.map((item, index) => {
          // Single item is always expanded; multiple items respect the open index
          const isOpen = isSingleItem || openIndex === index;
          const itemSlots = accordionVariants({ isOpen });

          // Fall back to the desktop image when no mobile image is authored
          const hasMobileImage = !!item.mobile_image?.url;
          const imageAsset = hasMobileImage ? item.mobile_image : item.desktop_image;
          const imageCslp = hasMobileImage ? item.$?.mobile_image : item.$?.desktop_image;

          return (
            <li key={index} className={itemSlots.accordionItem()}>
              {!isSingleItem && (
                <button
                  type="button"
                  className={itemSlots.accordionHeader()}
                  onClick={(e) => {
                    trackAdobeClick({ clickName: item.tab_title || `Accordion ${index + 1}` }, e);
                    onToggle(index);
                  }}
                  aria-expanded={isOpen}
                  tabIndex={0}
                  {...getCSLPAttributes(item.$?.tab_title)}
                >
                  <span className={itemSlots.accordionTitle()}>{item.tab_title}</span>
                  <i className={itemSlots.accordionIcon()} aria-hidden="true" />
                </button>
              )}

              <div className={itemSlots.accordionContent()}>
                <div className={itemSlots.accordionInner()}>
                  {imageAsset?.url && (
                    <div className={mobileSlots.imageWrapper()}>
                      <ImageWrapper
                        image={toEnhancedImage(imageAsset, {
                          alternateText: item.tab_title || 'Carousel image',
                          cslp: imageCslp,
                        })}
                        fill
                        wrapperClassName="w-full h-full"
                        imageClassName={mobileSlots.mobileImage()}
                        sizes="100vw"
                      />
                    </div>
                  )}

                  <div className={mobileSlots.mobileTextContent()}>
                    {item.eyebrow && (
                      <PlainTextWrapper
                        tag="p"
                        content={item.eyebrow}
                        className={mobileSlots.mobileEyebrow()}
                        cslpAttribute={item.$?.eyebrow}
                        isDangerouslySetInnerHTML
                      />
                    )}
                    {item.headline && (
                      <PlainTextWrapper
                        content={item.headline}
                        className={mobileSlots.mobileHeadline()}
                        cslpAttribute={item.$?.headline}
                        isDangerouslySetInnerHTML
                      />
                    )}
                    {item.body_copy && (
                      <div className={mobileSlots.mobileBodyCopy()}>
                        <RichTextWrapper
                          content={item.body_copy}
                          cslpAttribute={item.$?.body_copy}
                        />
                      </div>
                    )}
                    {item.cta?.cta &&
                      (item.cta?.cta?.link?.href ||
                        (item.cta?.cta?.page_reference &&
                          item.cta?.cta?.page_reference?.length > 0)) && (
                        <div className={mobileSlots.mobileCtaWrapper()}>
                          <ButtonWrapper cta={item.cta.cta} />
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const TextOverlayImageCarousel = (props: TextOverlayImageCarouselProps) => {
  const {
    componentName,
    _metadata,
    hide_component,
    subtitle,
    items,
    feature_panel,
    enable_bottom_spacing,
    $,
  } = props;
  const [activeIndex, setActiveIndex] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (hide_component) return null;

  if (!items || items.length === 0) return null;

  const handleTabChange = (index: number) => {
    if (index !== activeIndex) setActiveIndex(index);
  };

  const handleAccordionToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <Container
      componentName={componentName}
      metaId={_metadata?.uid}
      containerBleed
      horizontalPadding={false}
      bottomMargin={enable_bottom_spacing}
    >
      {isDesktop ? (
        <DesktopView items={items} activeIndex={activeIndex} onTabChange={handleTabChange} />
      ) : (
        <MobileView
          items={items}
          subtitle={subtitle}
          subtitleCslp={$}
          openIndex={openIndex}
          onToggle={handleAccordionToggle}
        />
      )}
      <RichTextWrapper content={feature_panel} {...getCSLPAttributes($?.feature_panel)} />
    </Container>
  );
};
